'use client';

import { AnimatePresence, motion, useInView } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface CaseStudyCardProps {
    videoSrc: string;
    logoSrc: string;
    company: string;
    description: string;
    tags: string[];
    path: string;
    thumbnailSrc?: string;
    country: string;
}

export default function CaseStudyCard({
    videoSrc,
    logoSrc,
    company,
    description,
    tags,
    path,
    thumbnailSrc,
}: CaseStudyCardProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const router = useRouter();

    // Scroll animation trigger
    const isInView = useInView(cardRef, { once: true, margin: '-100px' });

    // Video visibility trigger (for autoplay control) - doesn't use "once" so it tracks entry/exit
    const isVideoInView = useInView(cardRef, { margin: '-50px' });

    // Screen size detection for lg breakpoint (1024px)
    useEffect(() => {
        const checkScreenSize = () => {
            setIsSmallScreen(window.innerWidth < 1024);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Prefetch the target route when in view and on hover for instant navigation
    useEffect(() => {
        if (isInView) {
            try { router.prefetch(path); } catch { }
        }
    }, [isInView, path, router]);

    // Preload video when card enters viewport to make hover play instant
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (isInView) {
            try {
                video.preload = 'auto';
                video.muted = true;
                video.load();
            } catch { }
        }
    }, [isInView]);

    // Autoplay video on small screens when in viewport
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !isInView || !isSmallScreen) return;

        if (isVideoInView) {
            // Video is in viewport - play it
            const playVideoOnSmallScreen = async () => {
                try {
                    video.muted = true;
                    video.currentTime = 0;
                    await video.play();
                } catch {
                    try {
                        video.muted = true;
                        video.load();
                        await video.play();
                    } catch { }
                }
            };

            if (video.readyState < 2) {
                const onCanPlay = () => {
                    video.removeEventListener('canplay', onCanPlay);
                    playVideoOnSmallScreen();
                };
                video.addEventListener('canplay', onCanPlay);
            } else {
                void playVideoOnSmallScreen();
            }
        } else {
            // Video is out of viewport - pause it
            try {
                video.pause();
                video.currentTime = 0;
            } catch { }
        }

        return () => {
            if (isSmallScreen && video) {
                try { video.pause(); } catch { }
            }
        };
    }, [isInView, isSmallScreen, isVideoInView]);

    const handleMouseEnter = () => {
        setIsHovered(true);
        try { router.prefetch(path); } catch { }

        // Only handle hover video play on larger screens
        // On small screens, video is already autoplaying
        if (isSmallScreen) return;

        const video = videoRef.current;
        if (!video) return;
        video.muted = true;
        const playSafely = async () => {
            try {
                try { video.currentTime = 0; } catch { }
                await video.play();
            } catch {
                try {
                    video.muted = true;
                    video.load();
                    await video.play();
                } catch { }
            }
        };
        if (video.readyState < 2) {
            const onCanPlay = () => {
                video.removeEventListener('canplay', onCanPlay);
                playSafely();
            };
            video.addEventListener('canplay', onCanPlay);
        } else {
            void playSafely();
        }
    };

    const handleMouseLeave = () => {
        setIsHovered(false);

        // Only handle hover video pause on larger screens
        // On small screens, let video continue autoplaying
        if (isSmallScreen) return;

        const video = videoRef.current;
        if (!video) return;
        try { video.pause(); } catch { }
        try { video.currentTime = 0; } catch { }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
    };

    return (
        <>
            {/* Hover Label */}
            <AnimatePresence>
                {isHovered && (
                    <>
                        {/* Expanded Label pill - dark glass, no cursor dot */}
                        <motion.div
                            key="hover-label"
                            initial={{ opacity: 0, y: 8, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 6, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className="fixed z-[9999] xl:pointer-events-none xl:block hidden"
                            style={{ left: mousePosition.x + 18, top: mousePosition.y - 52 }}
                        >
                            <div className="relative">
                                {/* matte solid pill (no glass) */}
                                <div className="relative flex items-center gap-3 px-6 py-3 rounded-full bg-slate-700 text-slate-100 border border-slate-700 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.7)]">
                                    <span className="relative flex items-center justify-center w-3 h-3 rounded-full bg-white/85">
                                        <span className="absolute inline-flex w-3 h-3 rounded-full bg-white/80 animate-pulse" />
                                    </span>
                                    <span className="text-sm md:text-base font-semibold tracking-wide">View Case Study</span>
                                    <span className="w-5 h-5">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12h14" />
                                            <path d="M12 5l7 7-7 7" />
                                        </svg>
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            <Link href={path} prefetch>
                <motion.div
                    ref={cardRef}
                    initial={{ opacity: 0, y: 60 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
                    transition={{
                        duration: 0.8,
                        ease: 'easeOut',
                        delay: 0.1,
                    }}
                    className="w-full shadow-[0_4px_54px_0_rgba(0,0,0,0.05)] bg-white p-5 flex flex-col relative xl:cursor-none"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                >
                    {/* Video Section - Smaller and at top */}
                    <motion.div
                        className="relative w-full  h-[200px] h-[200px] xl:h-[400px] border-1 border-gray-100   mb-6 overflow-hidden "
                        whileHover={{
                            scale: 1.02,
                            transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                    >
                        {/* Thumbnail overlay shown when not hovering on large screens or when video not ready */}
                        {thumbnailSrc && (
                            <motion.img
                                src={thumbnailSrc}
                                alt={`${company} thumbnail`}
                                className="absolute inset-0 w-full h-full object-fill z-10 pointer-events-none select-none"
                                initial={false}
                                animate={{
                                    opacity:
                                        (isHovered && isVideoReady && !isSmallScreen) ||
                                            (isSmallScreen && isVideoReady && isVideoInView)
                                            ? 0
                                            : 1,
                                }}
                                transition={{ duration: 0.25, ease: 'easeOut' }}
                                draggable={false}
                            />
                        )}
                        <motion.video
                            ref={videoRef}
                            src={videoSrc}
                            className="shadow-md w-full h-full object-cover"
                            preload="metadata"
                            poster={thumbnailSrc}
                            muted
                            loop
                            playsInline
                            onLoadedData={() => setIsVideoReady(true)}
                            onCanPlay={() => setIsVideoReady(true)}
                            initial={false}
                            animate={{
                                opacity:
                                    (isHovered && isVideoReady && !isSmallScreen) ||
                                        (isSmallScreen && isVideoReady && isVideoInView)
                                        ? 1
                                        : 0.0001,
                            }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            whileHover={{
                                scale: 1.05,
                                transition: { duration: 0.4, ease: 'easeOut' },
                            }}
                        />
                        {/* Subtle overlay on hover */}
                        <motion.div
                            className="absolute inset-0 bg-black/0 z-20"
                            whileHover={{
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                transition: { duration: 0.3 },
                            }}
                        />
                    </motion.div>

                    {/* Content Section - Below video */}
                    <motion.div
                        className="flex-1 flex flex-col justify-end"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{
                            duration: 0.6,
                            ease: 'easeOut',
                            delay: 0.3,
                        }}
                    >
                        <div className="grid grid-cols-12 items-start gap-4">
                            {/* Logo section */}
                           

                            {/* Text section */}
                            <div className="grid col-span-12 grid-cols-4  gap-12">
                              <div className='col-span-3'>
                              <motion.h3
                                    className="text-lg sm:text-xl 2xl:text-2xl font-semibold text-[#272d57]"
                                    whileHover={{
                                        x: 5,
                                        transition: { duration: 0.2 },
                                    }}
                                >
                                    {company}
                                </motion.h3>

                                <motion.p
                                    className="text-[#1b1b1b] leading-snug   2xl:text-base text-sm my-2"
                                    whileHover={{
                                        x: 5,
                                        transition: { duration: 0.2 },
                                    }}
                                >
                                    {description}
                                </motion.p>
                              </div>

                              <motion.div
                                className=""
                                whileHover={{
                                    scale: 1.1,
                                    transition: { duration: 0.2 },
                                }}
                            >
                                <Image
                                    src={logoSrc}
                                    alt={`${company} Logo`}
                                    width={80}
                                    height={80}
                                    className="w-auto h-5 xl:size-20 object-contain"
                                />
                            </motion.div>

                             
                            </div>

                            <div className="flex col-span-12 flex-wrap gap-2">
                                    {tags.map((tag, index) => (
                                        <motion.span
                                            key={index}
                                            className="px-4 py-1 rounded bg-gray-100 text-xs sm:text-sm  text-sm font-medium text-gray-800"
                                            whileHover={{
                                                scale: 1.05,
                                                backgroundColor: '#e5e7eb',
                                                transition: { duration: 0.2 },
                                            }}
                                        >
                                            {tag}
                                        </motion.span>
                                    ))}
                                </div>
                        </div>
                    </motion.div>
                </motion.div>
            </Link>
        </>
    );
}